// Workshop — project workspaces where the operator and agents co-produce
// markdown artifacts. Mirrors the backend WorkshopModel DTOs; statuses/kinds
// are strings (TS-level enums, never DB enums).

import type {
  AspectRatio,
  CalendarMediaType,
  CalendarOrientation,
  CalendarPlatform,
  CalendarVideoLength,
  IdeaReference,
  IdeaSource,
} from "@/types/contentCalendar.type";
import type { BriefRevision, CalendarBriefKind } from "@/types/productionBrief.type";

export type WorkshopProjectStatus = "active" | "archived";

/**
 * Who made a project item, resolved server-side for the signed-in viewer.
 * `actor` is who wrote it; `userName` is that person, the one the assistant
 * worked for, or the one who deployed the agent.
 */
export type WorkshopAttribution = {
  actor: "user" | "assistant" | "agent";
  userId: string | null;
  userName: string | null;
  /**
   * Threads are private: only threads the viewer is in ("own": theirs, or a
   * shared thread they are part of) carry a title.
   */
  thread: { id: string; title: string | null; state: "own" | "other" | "deleted" } | null;
  /** Null name = the deployment has since been deleted. */
  deployment: { id: string | null; name: string | null } | null;
  at: string;
};

/**
 * Every item read from the API carries this. Optional on the types because an
 * optimistic cache entry exists before the server has attributed it.
 */
export type WorkshopItemAttribution = {
  createdBy: WorkshopAttribution;
  /** Artifacts only: the newest revision's author, null while still on v1. */
  lastEditedBy?: WorkshopAttribution | null;
};

/** A person as the workshop shows them. `name` is null for an unknown user. */
export type WorkshopPerson = {
  userId: string;
  name: string | null;
};

/** Someone with a shared thread open right now; "away" = their tab is hidden. */
export type WorkshopPresentPerson = WorkshopPerson & {
  state: "active" | "away";
};

export type WorkshopProject = {
  id: string;
  brandId: string;
  name: string;
  description: string | null;
  status: WorkshopProjectStatus | string;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  artifactCount?: number;
  deploymentCount?: number;
  /** Threads the viewer can open: their own and shared ones they are in. */
  sessionCount?: number;
  /** The viewer created the project: only they manage members, settings and deletion. */
  isOwner?: boolean;
  owner?: WorkshopPerson;
  /** People the owner added. Empty = the project is private to its owner. */
  members?: WorkshopPerson[];
  /**
   * Collaborators in a thread the viewer shares with them, right now. Never
   * the viewer. Only on the project list.
   */
  activeCollaborators?: WorkshopPresentPerson[];
};

/** GET/POST/DELETE /workshop/projects/:id/members. */
export type WorkshopProjectMembers = {
  owner: WorkshopPerson;
  members: Array<WorkshopPerson & { email: string | null; addedAt: string }>;
};

/**
 * A project image's standing in the brand library, when it has one.
 * "copied_to": someone copied it there. "copied_from": it was added from there.
 * Either way a copy to the library would be a duplicate.
 */
export type WorkshopMediaLibraryLink = {
  /** The brand-library image. */
  mediaId: string;
  relation: "copied_to" | "copied_from";
  /** Who put that library image there, and when. */
  by: WorkshopAttribution;
};

/**
 * A PDF or video the project owns — uploaded in any of its threads or in the
 * Sources tab. Every thread (and every deployed agent) can read it.
 */
export type WorkshopFile = {
  id: string;
  fileName: string | null;
  type: "pdf" | "video" | "spreadsheet" | "text" | string;
  state: "processing" | "completed" | "failed" | string;
  createdAt: string;
  durationSec: number | null;
  /** Client-safe failure copy; null unless failed. */
  error: string | null;
  canRetry: boolean;
  /** The thread it was attached in; null for a Sources-tab upload. */
  sessionId: string | null;
  /** Set for a video the assistant attached from a link: the post it came from. */
  sourceUrl?: string | null;
  attribution?: WorkshopItemAttribution;
};

export type WorkshopContextItemKind =
  | "universe_fact"
  | "strategy"
  | "market_signal"
  | "trend"
  | "document"
  | "url";

export type WorkshopContextItem = {
  id: string;
  projectId: string;
  kind: WorkshopContextItemKind | string;
  targetId: string | null;
  url: string | null;
  label: string;
  note: string | null;
  addedByUserId: string;
  createdAt: string;
  attribution?: WorkshopItemAttribution;
};

export const WORKSHOP_CONTEXT_KINDS: WorkshopContextItemKind[] = [
  "universe_fact",
  "strategy",
  "market_signal",
  "trend",
  "document",
  "url",
];

export const WORKSHOP_CONTEXT_KIND_LABELS: Record<WorkshopContextItemKind, string> = {
  universe_fact: "Universe facts",
  strategy: "Strategies",
  market_signal: "Market signals",
  trend: "Trends",
  document: "Documents",
  url: "Links",
};

export type WorkshopArtifactListItem = {
  id: string;
  title: string;
  kind: string;
  version: number;
  status: "active" | "archived" | string;
  updatedAt: string;
  attribution?: WorkshopItemAttribution;
  /** Notes on it nobody has resolved yet. */
  openNoteCount?: number;
};

export type WorkshopArtifactNoteStatus = "open" | "resolved";

/**
 * A note pinned to a passage of an artifact. The passage is the selected text
 * as rendered (`quote`) plus the words around it, so it can be found again
 * after the document changes.
 */
export type WorkshopArtifactNote = {
  id: string;
  artifactId: string;
  projectId: string;
  quote: string;
  prefix: string;
  suffix: string;
  artifactVersion: number;
  body: string;
  status: WorkshopArtifactNoteStatus;
  createdByUserId: string;
  resolvedAt: string | null;
  /** The assistant's account of what it did, when it resolved the note. */
  resolution: string | null;
  createdAt: string;
  updatedAt: string;
  /** Absent on an optimistic entry the server has not attributed yet. */
  attribution?: {
    createdBy: WorkshopAttribution;
    resolvedBy: WorkshopAttribution | null;
  };
};

export type WorkshopArtifact = WorkshopArtifactListItem & {
  projectId: string;
  content: string;
  createdAt: string;
};

export type WorkshopArtifactRevision = {
  id: string;
  version: number;
  content: string;
  authorType: "user" | "assistant" | "agent" | string;
  authorUserId: string | null;
  sourceSessionId: string | null;
  sourceRunId: string | null;
  note: string | null;
  createdAt: string;
  attribution?: WorkshopItemAttribution;
};

/** ACTIVE ideas can be pushed onto the calendar; MIGRATED ones already were. */
export type WorkshopIdeaStatus = "ACTIVE" | "MIGRATED" | "ARCHIVED";

export type WorkshopIdeaListItem = {
  id: string;
  title: string;
  status: WorkshopIdeaStatus | string;
  mediaType: CalendarMediaType;
  channels: CalendarPlatform[];
  pillarName: string;
  /** A production brief has been written for this idea. */
  hasBrief: boolean;
  updatedAt: string;
  migratedAt: string | null;
  attribution?: WorkshopItemAttribution;
};

export type WorkshopIdeaBriefRevision = BriefRevision & {
  authorUserId: string | null;
  /** The chat session that wrote it, when the chat did. */
  sourceSessionId: string | null;
};

/**
 * An idea's production brief — the calendar brief's body and read shape, written
 * before the idea has a day. On migration its live body becomes the post's brief.
 */
export type WorkshopIdeaBrief = {
  id: string;
  ideaId: string;
  /** Frozen from the idea's media type; the idea's format is locked while it exists. */
  kind: CalendarBriefKind;
  /** The live version. */
  version: number;
  lastEditedByUserId: string | null;
  lastEditedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** The live body, as the single most-recent revision. */
  revisions: WorkshopIdeaBriefRevision[];
};

/**
 * A structured concept idea saved from the workshop chat. The creative fields
 * are the calendar idea's, deliberately — migrating one is a column-for-column
 * copy, and the same card renders both.
 */
export type WorkshopIdea = {
  id: string;
  projectId: string;
  brandId: string;
  title: string;
  synopsis: string;
  hooks: string[];
  objectiveClaim: string;
  sources: IdeaSource[];
  /** Hydrated server-side on every read — same shape as the calendar's. */
  reference: IdeaReference | null;
  channels: CalendarPlatform[];
  mediaType: CalendarMediaType;
  orientation: CalendarOrientation;
  videoLength: CalendarVideoLength | null;
  aspectRatio: AspectRatio;
  durationSeconds: number | null;
  /** The strategy pillar this idea serves; becomes the post's pillar on migration. */
  pillarId: string | null;
  pillarName: string;
  status: WorkshopIdeaStatus | string;
  migratedAt: string | null;
  /** Loose link: the slot may be deleted later and this stays put. */
  migratedSlotId: string | null;
  migratedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  /** Hydrated server-side, shot visuals resolved like the calendar's. Null until written. */
  brief: WorkshopIdeaBrief | null;
  attribution?: WorkshopItemAttribution;
};

/** What POST /workshop/ideas/:id/migrate answers with. */
export type WorkshopIdeaMigration = {
  idea: WorkshopIdea;
  slotId: string;
  planId: string;
  year: number;
  month: number;
  slotStatus: string;
  /** The idea's brief came across, so the post landed at the brief stage. */
  withBrief: boolean;
};

export type WorkshopSession = {
  id: string;
  projectId: string;
  title: string;
  /** null = "auto" (backend resolves the platform default). */
  model: string | null;
  reasoningEffort: string | null;
  compactedSummary: string | null;
  compactedAtMessageId: string | null;
  /** Skill keys the assistant has loaded and not exited. */
  enabledSkills: string[];
  lastMessageAt: string;
  createdAt: string;
  /** The thread's original poster: only they rename, share or delete it. */
  ownerUserId?: string | null;
  /**
   * Set once the OP shared the thread. A shared thread has one AI and several
   * people: sends return at once and replies arrive over the socket.
   */
  sharedAt?: string | null;
  participantIds?: string[];
  owner?: WorkshopPerson | null;
  /** Everyone in the thread besides the OP. */
  participants?: WorkshopPerson[];
  /** The viewer started this thread. */
  isOwner?: boolean;
  /** Shared threads: who has it open right now (the viewer included). */
  present?: WorkshopPresentPerson[];
  /** The chat list only: M7's latest reply as one plain line (null before it replies). */
  lastReplyPreview?: string | null;
};

/** Quick Create: a skill ("works with you") or an agent ("runs on its own"), as a person sees it. */
export type WorkshopQuickCreateKind = "skill" | "agent";

/** A Quick Create card: one enabled skill or agent, with its own name, description and picture. */
export type WorkshopQuickCreateItem = {
  kind: WorkshopQuickCreateKind;
  /** The skill's or agent definition's id. */
  id: string;
  key: string;
  name: string;
  description: string;
  imageUrl: string | null;
  /** The Creos page row it sits in; null falls under Other. */
  category: string | null;
  /** Leads the new chat and the dashboard (the first 4). */
  featured: boolean;
};

/** What a message keeps of each skill or agent it was sent with (its pills). */
export type WorkshopQuickCreateRef = {
  kind: WorkshopQuickCreateKind;
  key: string;
  /** Absent on messages sent before ids were kept. */
  id?: string;
  name: string;
  imageUrl: string | null;
};

/** A platform skill as the chat surface sees it — never the playbook body. */
export type WorkshopSkillSummary = {
  key: string;
  name: string;
  description: string;
};

export type WorkshopAgentDefinition = {
  id: string;
  key: string;
  name: string;
  description: string | null;
  enabled: boolean;
};

/** Workshop agents run once; "recurring" may still appear on legacy rows. */
export type WorkshopDeploymentMode = "once" | "recurring";

export type WorkshopDeployment = {
  id: string;
  projectId: string;
  definitionId: string;
  definitionKey?: string;
  definitionName?: string;
  name: string;
  objective: string;
  mode: WorkshopDeploymentMode | string;
  cadence: string | null;
  nextRunAt: string | null;
  enabled: boolean;
  lastRunId: string | null;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunSummary: string | null;
  lastError: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  attribution?: WorkshopItemAttribution;
};

export type WorkshopDeploymentRun = {
  id: string;
  status: string;
  scheduledAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  model: string | null;
};

/** One message of a deployed agent's conversation. */
export type WorkshopDeploymentMessage = {
  id: string;
  /**
   * "user": sent to the agent; "assistant": the agent's own turn; "stop": an
   * event, a person pressed Stop on a turn (no content).
   */
  role: "user" | "assistant" | "stop";
  content: string;
  status: string;
  runId: string | null;
  /** Tool names the agent called in this part of its turn. */
  tools: string[];
  /** Sent by the Workshop assistant (a chat thread) rather than a person. */
  fromSession: boolean;
  /** Still waiting for the agent to read it. */
  unread: boolean;
  /** "stop" rows: who pressed Stop (null name: a deleted user). */
  stoppedByUserId?: string | null;
  stoppedByName?: string | null;
  createdAt: string;
};

/** A run the worker still owes progress on — drives the fast poll tier. */
export function isDeploymentRunBusy(status: string | null | undefined): boolean {
  return status === "queued" || status === "running";
}
